// @flow
import * as React from 'react'
import type { FormWithPdf } from 'types/form'

import { useFilters, useUpdateURLSearchParams } from '@/hooks/useQuery'
import { useLoadFormsAndDocuments } from '@/queries/forms/useLoadForms'

type FormsContextProps = {
  page: number,
  pageSize: number,
  total: number,
  loading: boolean,
  resources: FormWithPdf[],
  search: string,
  nextPage: () => void,
  prevPage: () => void,
  setPageSize: (pageSize: number) => void,
  makeSearch: (search: string) => void,
  fetchFormPage: () => Promise<void>,
  startLoading: () => void,
  stopLoading: () => void,
}

export const FormsContext: React.Context<FormsContextProps> =
  React.createContext<FormsContextProps>({
    page: 1,
    pageSize: 25,
    total: 0,
    loading: true,
    resources: [],
    search: '',
    nextPage: () => {},
    prevPage: () => {},
    setPageSize: () => {},
    makeSearch: () => {},
    fetchFormPage: () => Promise.resolve(),
    startLoading: () => {},
    stopLoading: () => {},
  })

type Props = {
  children: React.Node,
  tabletMode: boolean,
}

export function FormsProvider({ children, tabletMode }: Props): React.Node {
  const updateParams = useUpdateURLSearchParams()
  const { filters } = useFilters({
    defaultValues: { page: 1, pageSize: 25, search: '' },
  })
  const [loading, setLoading] = React.useState<boolean>(false)
  const {
    data: forms,
    isLoading,
    isFetching,
    refetch,
  } = useLoadFormsAndDocuments(
    {
      ...filters,
      without_disabled: tabletMode ? 'true' : 'false',
    },
    {
      keepPreviousData: true,
    }
  )
  const total = forms?.count || 0
  const page = forms?.page || 1
  const pageSize = forms?.pageSize || 25
  const search = filters.search || ''

  const makeSearch = React.useCallback(
    (search: string) => {
      updateParams(['search', search], ['page', '1'])
    },
    [updateParams]
  )

  const nextPage = React.useCallback(() => {
    if (forms?.next) {
      updateParams(['page', forms.next])
    }
  }, [forms?.next, updateParams])

  const prevPage = React.useCallback(() => {
    if (forms?.previous) {
      updateParams(['page', forms.previous])
    }
  }, [forms?.previous, updateParams])

  const setPageSize = React.useCallback(
    (pageSize: number) => {
      updateParams(['pageSize', pageSize])
    },
    [updateParams]
  )

  const fetchFormPage = React.useCallback(async () => {
    await refetch()
  }, [refetch])

  const props = React.useMemo(
    () => ({
      page,
      pageSize,
      total,
      loading: loading || isLoading || isFetching,
      search,
      resources: forms?.results || [],
      nextPage,
      prevPage,
      setPageSize,
      makeSearch,
      fetchFormPage,
      startLoading: () => setLoading(true),
      stopLoading: () => setLoading(false),
    }),
    [
      fetchFormPage,
      forms?.results,
      isFetching,
      isLoading,
      loading,
      makeSearch,
      nextPage,
      page,
      pageSize,
      prevPage,
      search,
      setPageSize,
      total,
    ]
  )

  return <FormsContext.Provider value={props}>{children}</FormsContext.Provider>
}
